import { ApiResponse, axiosInstance } from '@/lib/axios';
import { FetchParams, IPagination } from '@/types/general.type';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { QueryConfig } from '@/lib/react-query';
import { TChannel } from '../../_shared/types/channel.type';

export type TGetListIntegrationNolimitEngageResponse = ApiResponse<IPagination<TChannel>>;
export type TGetListIntegrationNolimitEngageParams = FetchParams;

export const getListIntegrationNolimitEngage = async (
  params: TGetListIntegrationNolimitEngageParams
): Promise<TGetListIntegrationNolimitEngageResponse> => {
  const url = '/integration/engage/org-channel';

  return axiosInstance.get(url, { params });
};

export const GET_LIST_INTEGRATION_NOLIMIT_ENGAGE_QUERY_KEY = 'get-list-integration-nolimit-engage';

export const getListIntegrationNolimitEngageQueryOptions = <TData = IPagination<TChannel>>(
  params: TGetListIntegrationNolimitEngageParams,
  select?: (data: TGetListIntegrationNolimitEngageResponse) => TData
) => {
  return queryOptions({
    queryKey: [
      GET_LIST_INTEGRATION_NOLIMIT_ENGAGE_QUERY_KEY,
      {
        page: params?.page,
        limit: params?.limit,
        search: params?.search,
        sortDir: params?.sortDir,
      },
    ],
    queryFn: () => getListIntegrationNolimitEngage(params),
    select,
  });
};

// Overload when select is provided - returns transformed type
export function useGetListIntegrationNolimitEngageQuery<TData>(options: {
  params: TGetListIntegrationNolimitEngageParams;
  queryConfig?: QueryConfig<typeof getListIntegrationNolimitEngageQueryOptions<TData>>;
  select: (data: TGetListIntegrationNolimitEngageResponse) => TData;
}): ReturnType<typeof useQuery<TData>>;

// Overload when select is not provided - returns raw API response (even if generic is provided)
export function useGetListIntegrationNolimitEngageQuery(options: {
  params: TGetListIntegrationNolimitEngageParams;
  queryConfig?: QueryConfig<
    typeof getListIntegrationNolimitEngageQueryOptions<TGetListIntegrationNolimitEngageResponse>
  >;
}): ReturnType<typeof useQuery<TGetListIntegrationNolimitEngageResponse>>;

// Implementation
export function useGetListIntegrationNolimitEngageQuery<TData = TGetListIntegrationNolimitEngageResponse>({
  queryConfig,
  params,
  select,
}: {
  params: TGetListIntegrationNolimitEngageParams;
  queryConfig?: QueryConfig<
    typeof getListIntegrationNolimitEngageQueryOptions<TData | TGetListIntegrationNolimitEngageResponse>
  >;
  select?: (data: TGetListIntegrationNolimitEngageResponse) => TData;
}) {
  return useQuery({
    ...getListIntegrationNolimitEngageQueryOptions(params, select),
    ...queryConfig,
  }) as ReturnType<
    typeof useQuery<
      TData extends TGetListIntegrationNolimitEngageResponse ? TGetListIntegrationNolimitEngageResponse : TData
    >
  >;
}
